"use client";

import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { EASE_OUT_EXPO } from "../../motion/reveal";

// =============================================================================

/**
 * Classes of a control rendered inside a LineField: transparent, borderless,
 * large text sitting on the animated underline.
 */
export const lineControlClassName = cn(
  "block w-full min-w-0 resize-none bg-transparent pb-[1.1rem] text-[clamp(1.0625rem,1.67vw,1.5rem)] leading-snug text-foreground caret-brand outline-none",
  "placeholder:text-muted-foreground/90 placeholder:transition-opacity placeholder:duration-500 focus:placeholder:opacity-60",
  "read-only:cursor-default disabled:cursor-not-allowed disabled:opacity-60",
  // The underline shows the focus: no outline on the control itself
  "focus-visible:outline-none"
);

/** Accessibility attributes linking a control to its LineField */
export function lineControlProps(id: string, error?: string) {
  return {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
  } as const;
}

// =============================================================================

type FieldLabelProps = {
  /** Id of the labelled control */
  htmlFor: string;
  label: string;
  required?: boolean;
  /**
   * "span" for controls that can't be labelled by a <label> (radio group…):
   * reference it with aria-labelledby={`${htmlFor}-label`}
   */
  as?: "label" | "span";
  className?: string;
};

/**
 * Big label of the form fields, with the coral asterisk of required fields.
 * Its id is `${htmlFor}-label`.
 */
export function FieldLabel({
  htmlFor,
  label,
  required = false,
  as: Tag = "label",
  className,
}: FieldLabelProps) {
  return (
    <Tag
      id={`${htmlFor}-label`}
      htmlFor={Tag === "label" ? htmlFor : undefined}
      className={cn(
        "text-[clamp(1.375rem,2.1vw,1.875rem)] leading-tight text-foreground",
        className
      )}
    >
      {label}
      {required && (
        <span aria-hidden className="ml-0.5 text-brand">
          *
        </span>
      )}
    </Tag>
  );
}

// =============================================================================

type LineFieldProps = {
  /** Id of the control, used by the label and the error message */
  htmlFor: string;
  label: string;
  required?: boolean;
  labelAs?: FieldLabelProps["as"];
  /** Translated error message */
  error?: string;
  /** Rendered next to the label (counter, hint…) */
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
};

/**
 * Form field of the design: big label with a coral asterisk, borderless
 * control and an underline. A second line draws itself from the left on
 * focus (and retracts to the right on blur); it turns coral on error.
 */
export function LineField({
  htmlFor,
  label,
  required = false,
  labelAs,
  error,
  aside,
  className,
  children,
}: LineFieldProps) {
  return (
    <div
      data-invalid={error ? "" : undefined}
      className={cn("group/field relative", className)}
    >
      <div className="flex items-end justify-between gap-4">
        <FieldLabel
          htmlFor={htmlFor}
          label={label}
          required={required}
          as={labelAs}
        />
        {aside}
      </div>

      <div className="relative mt-5 md:mt-7">
        {children}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-foreground/45"
        />
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-px origin-right scale-x-0 bg-foreground transition-transform duration-700 ease-out-expo",
            "group-focus-within/field:origin-left group-focus-within/field:scale-x-100",
            "group-data-invalid/field:origin-left group-data-invalid/field:scale-x-100 group-data-invalid/field:bg-brand"
          )}
        />
      </div>

      <FieldError id={`${htmlFor}-error`} message={error} />
    </div>
  );
}

// =============================================================================

/**
 * Error message under the underline. Absolutely positioned in the gap below
 * the field so that it never shifts the layout.
 */
export function FieldError({ id, message }: { id: string; message?: string }) {
  return (
    <div id={id} aria-live="polite" className="absolute top-full left-0 mt-2">
      <AnimatePresence initial={false}>
        {message && (
          <motion.p
            key={message}
            initial={{ opacity: 0, y: -6, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -4, transition: { duration: 0.2 } }}
            transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
            className="text-sm text-brand"
          >
            {message}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
